/* Screens: home, analyse, compare, result, recent and "how we grade".
   Each takes the <main> element and fills it. */

import { analyse, describeFailure, extract } from "./api";
import { escapeHtml, html, qs, setBusy, toast } from "./dom";
import { NOVA_SHORT, SIGA_NAMES, gauge, novaColour, sigaColour } from "./grade";
import {
  addHistory,
  clearHistory,
  findHistory,
  loadHistory,
  newId,
  timeAgo,
  type HistoryEntry,
} from "./history";
import { icon } from "./icons";
import { ProductInput, type InputPreset } from "./product-input";
import { displayFor, renderAnalysis, renderComparison, swapComparison } from "./result";
import type { NovaGroup } from "./types";

export function go(path: string): void {
  if (location.hash === "#" + path) window.dispatchEvent(new HashChangeEvent("hashchange"));
  else location.hash = path;
}

/** In-memory extras for results from this session: photos and inputs, which
 *  can't go into localStorage. */
const session = new Map<string, { images: string[]; presets: InputPreset[] }>();

export function pageHead(title: string, back = "/", action = ""): string {
  return `<div class="page-head">
    <button class="icon-btn back" type="button" data-back="${escapeHtml(back)}" aria-label="Back">${icon("back")}</button>
    <h1>${escapeHtml(title)}</h1>
    <div class="page-head-action">${action}</div>
  </div>`;
}

export function wireBack(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>("[data-back]").forEach((b) => {
    b.addEventListener("click", () => go(b.dataset.back ?? "/"));
  });
}

/* ---------------- loading sheet ---------------- */

const TIPS = [
  "Checking Open Food Facts for the ingredient list…",
  "Reading the label text…",
  "Matching additives and INS codes…",
  "Working out the NOVA group…",
];

function busySheet(photos: number): () => void {
  const sheet = html(`<div class="busy-sheet" role="status" aria-live="polite">
    <div class="busy-card">
      <span class="busy-ring"></span>
      <strong>Analysing…</strong>
      <p class="busy-tip">${TIPS[0]}</p>
      ${photos ? `<small class="muted">Reading ${photos} photo${photos > 1 ? "s" : ""}. The first scan after a restart can take up to a minute while the OCR model loads.</small>` : ""}
    </div>
  </div>`);
  document.body.appendChild(sheet);
  let i = 0;
  const timer = window.setInterval(() => {
    i = (i + 1) % TIPS.length;
    qs(sheet, ".busy-tip").textContent = TIPS[i];
  }, 1800);
  return () => {
    window.clearInterval(timer);
    sheet.remove();
  };
}

/* ---------------- home ---------------- */

export function recentItem(e: HistoryEntry): string {
  if (e.kind === "analyse") {
    const g = e.data.safety.nova_group;
    return `<a class="recent" href="#/result/${e.id}">
      <span class="recent-score" style="--tone:${novaColour(g)}">${g}</span>
      <span class="recent-text"><strong>${escapeHtml(e.name)}</strong>
        <small>Analysed · ${escapeHtml(NOVA_SHORT[g])}${e.data.safety.siga_class ? " · Siga " + escapeHtml(e.data.safety.siga_class) : ""} · ${timeAgo(e.at)}</small></span>
      ${icon("arrow")}</a>`;
  }
  const a = e.data.khetika_safety.nova_group;
  const b = e.data.competitor_safety.nova_group;
  return `<a class="recent" href="#/result/${e.id}">
    <span class="recent-score pair"><i style="--tone:${novaColour(a)}">${a}</i><i style="--tone:${novaColour(b)}">${b}</i></span>
    <span class="recent-text"><strong>${escapeHtml(e.names[0])} vs ${escapeHtml(e.names[1])}</strong>
      <small>Compared · ${timeAgo(e.at)}</small></span>
    ${icon("arrow")}</a>`;
}

/* ---------------- analyse ---------------- */

let analyseInput: ProductInput | null = null;

function getAnalyseInput(onChange: () => void): ProductInput {
  analyseInput ??= new ProductInput({
    tag: "1",
    title: "Your product",
    subtitle: "Barcode or label photo",
    onChange: () => onChangeRef(),
  });
  onChangeRef = onChange;
  return analyseInput;
}
let onChangeRef: () => void = () => {};

export function analyseView(main: HTMLElement, params: URLSearchParams): void {
  main.innerHTML = `${pageHead("Analyse a product")}
    <p class="lede">Scan the barcode. Not found? Snap the label.</p>
    <div class="inputs"></div>
    <div class="cta-bar">
      <button class="btn primary big wide go-btn" type="button" disabled>
        ${icon("analyse")}<span class="btn-label">Analyse product</span></button>
      <p class="cta-hint muted small"></p>
    </div>`;
  wireBack(main);
  const button = qs<HTMLButtonElement>(main, ".go-btn");
  const hint = qs(main, ".cta-hint");
  const refresh = (): void => {
    const ready = input.ready();
    button.disabled = !ready;
    hint.textContent = ready
      ? input.found()
        ? "Ready"
        : "Ready to read the label"
      : input.missing();
  };
  const input = getAnalyseInput(refresh);
  qs(main, ".inputs").appendChild(input.root);
  refresh();

  if (params.get("scan") === "1") {
    history.replaceState(null, "", "#/analyse");
    void input.startScan();
  }
  const barcode = params.get("barcode");
  if (barcode) {
    // Arriving from a search result: look the product up straight away.
    history.replaceState(null, "", "#/analyse");
    void input.lookupCode(barcode);
  }

  button.addEventListener("click", async () => {
    const form = new FormData();
    input.appendTo(form, "product");
    setBusy(button, true);
    const done = busySheet(input.photoCount());
    try {
      const data = await analyse(form);
      const id = newId();
      const image = input.front ? URL.createObjectURL(input.front) : "";
      const display = displayFor(data.product, input.name(), "Your product", image);
      addHistory({ kind: "analyse", id, at: Date.now(), name: display.name, data });
      session.set(id, { images: [image], presets: [input.preset()] });
      go("/result/" + id);
    } catch (error) {
      toast(describeFailure(error), "error");
    } finally {
      done();
      setBusy(button, false);
      refresh();
    }
  });
}

/** Start the camera on the analyse screen (the bottom bar's Scan button). */
export function scanNow(): void {
  go("/analyse?scan=1");
}

/* ---------------- compare ---------------- */

let compareInputs: [ProductInput, ProductInput] | null = null;
let compareChange: () => void = () => {};

function getCompareInputs(): [ProductInput, ProductInput] {
  compareInputs ??= [
    new ProductInput({
      tag: "A",
      title: "Khetika product",
      subtitle: "Barcode or label photo",
      tone: "khetika",
      onChange: () => compareChange(),
    }),
    new ProductInput({
      tag: "B",
      title: "Competitor product",
      subtitle: "Barcode or label photo",
      tone: "competitor",
      onChange: () => compareChange(),
    }),
  ];
  return compareInputs;
}

/** Load a product into the first compare slot, e.g. from a report. */
export function compareWith(preset: InputPreset): void {
  const [a, b] = getCompareInputs();
  go("/compare");
  void a.applyPreset(preset).then(() => b.root.scrollIntoView({ behavior: "smooth", block: "start" }));
}

export function compareView(main: HTMLElement): void {
  main.innerHTML = `${pageHead("Compare products")}
    <p class="lede">Add both products.</p>
    <div class="inputs two"></div>
    <div class="cta-bar">
      <button class="btn primary big wide go-btn" type="button" disabled>
        ${icon("compare")}<span class="btn-label">Compare products</span></button>
      <p class="cta-hint muted small"></p>
    </div>`;
  wireBack(main);
  const [a, b] = getCompareInputs();
  const button = qs<HTMLButtonElement>(main, ".go-btn");
  const hint = qs(main, ".cta-hint");
  compareChange = () => {
    const missing = [a, b].filter((p) => !p.ready()).length;
    button.disabled = missing > 0;
    hint.textContent = missing
      ? [a, b].map((p) => p.missing()).find(Boolean) ?? ""
      : "Ready";
  };
  const box = qs(main, ".inputs");
  box.append(a.root, html(`<div class="vs-divider"><span>vs</span></div>`), b.root);
  compareChange();

  button.addEventListener("click", async () => {
    const form = new FormData();
    a.appendTo(form, "khetika");
    b.appendTo(form, "competitor");
    const photos = [a, b].reduce((n, p) => n + p.photoCount(), 0);
    setBusy(button, true);
    const done = busySheet(photos);
    try {
      const data = await extract(form);
      const id = newId();
      const images = [a, b].map((p) => (p.front ? URL.createObjectURL(p.front) : ""));
      const da = displayFor(data.products.khetika, a.name(), "Khetika product", images[0]);
      const db = displayFor(data.products.competitor, b.name(), "Competitor product", images[1]);
      addHistory({ kind: "compare", id, at: Date.now(), names: [da.name, db.name], data });
      session.set(id, { images, presets: [a.preset(), b.preset()] });
      go("/result/" + id);
    } catch (error) {
      toast(describeFailure(error), "error");
    } finally {
      done();
      setBusy(button, false);
      compareChange();
    }
  });
}

/* ---------------- result ---------------- */

export function resultView(main: HTMLElement, id: string): void {
  const entry = findHistory(id);
  if (!entry) {
    main.innerHTML = `${pageHead("Result")}
      <section class="card empty">${icon("info", "ic big")}<h3>This result isn't available</h3>
      <p class="muted">Results are kept in this browser only. Run the analysis again.</p>
      <a class="btn primary" href="#/">Back to home</a></section>`;
    wireBack(main);
    return;
  }
  const extra = session.get(id);

  if (entry.kind === "analyse") {
    const d = displayFor(entry.data.product, entry.name, "Your product", extra?.images[0] ?? "");
    main.innerHTML = pageHead("Report", "/analyse");
    main.appendChild(renderAnalysis(d, entry.data));
    const preset: InputPreset = extra?.presets[0] ??
      (entry.data.product.source === "openfoodfacts" ? { barcode: entry.data.product.barcode } : {});
    main.appendChild(
      html(`<div class="float-bar">
        <button class="btn dark big" type="button" data-act="compare">${icon("compare")}<span>Compare</span></button>
        <button class="btn outline big" type="button" data-act="next">${icon("scan")}<span>Analyse next</span></button>
      </div>`),
    );
    qs(main, "[data-act=compare]").addEventListener("click", () => compareWith({ ...preset, name: entry.name }));
    qs(main, "[data-act=next]").addEventListener("click", () => {
      analyseInput = null;
      go("/analyse");
    });
  } else {
    let data = entry.data;
    let names = [...entry.names] as [string, string];
    let images = extra?.images ?? ["", ""];
    const draw = (): void => {
      main.innerHTML = pageHead(
        "Comparison",
        "/compare",
        `<button class="icon-btn swap" type="button" aria-label="Swap sides">${icon("swap")}</button>`,
      );
      const da = displayFor(data.products.khetika, names[0], "Product A", images[0]);
      const db = displayFor(data.products.competitor, names[1], "Product B", images[1]);
      main.appendChild(renderComparison(data, da, db));
      main.appendChild(
        html(`<div class="float-bar">
          <button class="btn dark big" type="button" data-act="new">${icon("compare")}<span>New comparison</span></button>
        </div>`),
      );
      qs(main, "[data-act=new]").addEventListener("click", () => {
        compareInputs = null;
        go("/compare");
      });
      qs(main, ".swap").addEventListener("click", () => {
        data = swapComparison(data);
        names = [names[1], names[0]];
        images = [images[1] ?? "", images[0] ?? ""];
        draw();
      });
      wireBack(main);
    };
    draw();
    return;
  }
  wireBack(main);
}

/* ---------------- recent ---------------- */

export function recentView(main: HTMLElement): void {
  const list = loadHistory();
  main.innerHTML = `${pageHead("Recent", "/", list.length ? `<button class="link clear" type="button">Clear</button>` : "")}
    ${
      list.length
        ? `<div class="recent-list">${list.map(recentItem).join("")}</div>
           <p class="muted small center">Saved in this browser only.</p>`
        : `<section class="card empty">${icon("clock", "ic big")}<h3>Nothing yet</h3>
           <p class="muted">Analyses and comparisons you run show up here.</p>
           <a class="btn primary" href="#/analyse">Analyse a product</a></section>`
    }`;
  wireBack(main);
  main.querySelector(".clear")?.addEventListener("click", () => {
    clearHistory();
    recentView(main);
  });
}

/* ---------------- how we grade ---------------- */

export function howView(main: HTMLElement): void {
  const rows: [NovaGroup, string][] = [
    [1, "Simple ingredients only. Nothing added beyond the ingredients themselves."],
    [2, "Culinary ingredients such as salt, sugar or oil, on their own."],
    [3, "Simple ingredients plus salt, sugar, oil or permitted preservatives and acidity regulators."],
    [4, "Declares colours, flavour enhancers, flavourings, sweeteners, anti-caking agents, emulsifiers, fillers such as maltodextrin, or hydrogenated fat."],
  ];
  const siga: [string, number, string][] = [
    ["A0", 100, "Unprocessed: no declared additives, no salt, sugar or oil."],
    ["A1", 90, "Minimally processed: simple, plain ingredients only."],
    ["B1", 75, "Processed (salt, sugar, oil or non-marker additives), with salt, sugar and fat all at or under the cut-offs."],
    ["B2", 60, "Processed, with salt, sugar or fat over a cut-off."],
    ["C0.1", 50, "Ultra-processed on a single marker, nutritionally balanced."],
    ["C0.2", 40, "Ultra-processed on a single marker, with salt, sugar or fat over a cut-off."],
    ["C1", 30, "Two to five markers of ultra-processing."],
    ["C2", 15, "More than five markers, or one artificial marker, or one at-risk additive."],
    ["C3", 0, "More than one artificial marker, or more than one at-risk additive."],
  ];
  const grades: [string, string][] = [["A", "85 and over"], ["B", "65 to 84"], ["C", "45 to 64"], ["D", "25 to 44"], ["E", "under 25"]];
  // Cited by authors, journal and year; a link only where the DOI or page is known.
  const refs: [string, string, string][] = [
    ["Monteiro CA, Cannon G, Levy RB, et al. Ultra-processed foods: what they are and how to identify them. Public Health Nutrition 22(5), 2019.",
      "NOVA groups", "https://doi.org/10.1017/S1368980018003762"],
    ["Davidou S, Christodoulou A, Fardet A, Frank K. Siga classification of foods by degree of processing. Food & Function, 2020.",
      "Siga classes", ""],
    ["Davidou S, Christodoulou A, Fardet A, Frank K. Siga classification, 2021 update. Journal of Food Composition and Analysis, 2021.",
      "Siga classes and markers", "https://doi.org/10.1016/j.jfca.2021.103848"],
    ["Review of food-processing classification systems that includes Siga. Food & Nutrition Research, 2025.",
      "Siga summary", "https://pmc.ncbi.nlm.nih.gov/articles/PMC12255158/"],
    ["UK Food Standards Agency front-of-pack nutrition labelling: medium thresholds per 100 g (salt 1.5 g, sugars 12.5 g, fat 17.5 g).",
      "Salt, sugar and fat cut-offs", ""],
    ["Open Food Facts product data and NOVA markers (ODbL licence).",
      "Barcode data and NOVA markers", "https://world.openfoodfacts.org/data"],
  ];
  main.innerHTML = `${pageHead("How we grade")}
    <section class="card">
      <p>Every product is placed in a <strong>NOVA group</strong>, the food-processing classification
        used in nutrition research. It is based only on what the label declares.</p>
      <div class="how-list">
        ${rows
          .map(
            ([g, text]) => `<div class="how-row">${gauge(g, "sm")}<div><strong>${NOVA_SHORT[g]}</strong>
              <p class="muted">${escapeHtml(text)}</p></div></div>`,
          )
          .join("")}
      </div>
    </section>
    <section class="card">
      <h3>Siga class</h3>
      <p class="muted"><strong>Siga</strong> refines NOVA's ultra-processed group by counting <em>markers of ultra-processing</em>
        (additives and ingredients found almost only in ultra-processed food) and by checking salt, sugar and fat.
        It is shown next to NOVA on every result.</p>
      <div class="how-list">
        ${siga
          .map(
            ([cls, score, text]) => `<div class="how-row siga-row"><span class="siga-chip" style="--c:${sigaColour(cls)}">${cls}</span>
              <div><strong>${escapeHtml(SIGA_NAMES[cls] ?? cls)} <span class="muted">· ${score}/100</span></strong>
              <p class="muted">${escapeHtml(text)}</p></div></div>`,
          )
          .join("")}
      </div>
      <h4>Markers</h4>
      <p class="muted"><strong>MUP1</strong>: substances identical to natural ones but made industrially, such as modified starch or natural flavouring.
        <strong>MUP2</strong>: artificial substances, such as artificial colours, sweeteners and flavours.
        <strong>At-risk additives</strong>: those with safety concerns raised by EFSA, such as sodium nitrite or titanium dioxide.</p>
      <h4>Salt, sugar and fat cut-offs</h4>
      <p class="muted">Per 100 g, a product is "high" above <strong>1.5 g salt</strong>, <strong>12.5 g sugars</strong> or
        <strong>17.5 g fat</strong>. The values come from Open Food Facts, or from the nutrition panel photo you upload.
        A high salt or sugars value also marks a product as processed when the label text doesn't say why.
        Without nutrition values we show the class without its balanced/high split (for example "C0") and an estimated score.</p>
    </section>
    <section class="card">
      <h3>The 0-100 reading and A-E grade</h3>
      <p class="muted">The 0-100 number is <strong>CleanScore's own</strong> fixed mapping of the Siga class, not part of Siga:
        the same class always gives the same number, so every point lost can be traced to a stated reason.
        When the class is only partly known, the number is the midpoint of the two possible values and is marked "about".</p>
      <div class="grade-bands">
        ${grades.map(([g, r]) => `<span class="grade-band"><b class="g-${g.toLowerCase()}">${g}</b>${r}</span>`).join("")}
      </div>
    </section>
    <section class="card">
      <h3>Where the data comes from</h3>
      <p class="muted">Barcodes are looked up in <a class="link" href="https://world.openfoodfacts.org" target="_blank" rel="noopener">Open Food Facts</a>.
        When it already has a NOVA group for the product, that group is shown, and our own reading is listed alongside
        when the two disagree. Products it doesn't list are read from photos: ingredients and front of pack, plus the nutrition panel if you have it.
        What we learn from those photos is kept in the CleanScore database, so the same barcode is found next time without photos.</p>
    </section>
    <section class="card">
      <h3>Good to know</h3>
      <p class="muted">NOVA and Siga are research classifications, not Indian regulatory ratings. Our Siga reading is
        <strong>Siga-based, applied to label data</strong>: it is not the official Siga tool, and our lists of markers
        are an approximate subset of the published ones. The additives flagged are permitted in India at regulated levels.
        Photo readings can contain mistakes, so check them against the pack. A label that lists nothing may simply be less
        detailed. Nothing here is a medical claim.</p>
    </section>
    <section class="card">
      <h3>References</h3>
      <ol class="refs">
        ${refs
          .map(
            ([cite, used, url]) => `<li>${escapeHtml(cite)} <span class="muted">Used for: ${escapeHtml(used)}.</span>
              ${url ? `<a class="link" href="${url}" target="_blank" rel="noopener">Open</a>` : ""}</li>`,
          )
          .join("")}
      </ol>
    </section>`;
  wireBack(main);
}
